import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { API_BASE } from '../api-base.token';
import { DeviceResponse } from './device-response';
import { DeviceEventResponse } from './device-event-response';
import { DeviceRequest } from './device-request';
import { UpdateDeviceRequest } from './update-device-request';
import { PagedResult } from './paged-result';
import { DeviceEventsQuery } from './device-events-query';
import { DeviceProjectionResponse } from './device-projection-response';
import { DistanceReadingResponse } from './distance-reading-response';
import { DistanceReadingsQuery } from './distance-readings-query';

@Injectable({
  providedIn: 'root',
})
export class DevicesHttpService {
  constructor(
    private readonly httpClient: HttpClient,
    @Inject(API_BASE) private readonly apiBase: string
  ) {}

  get(): Observable<DeviceResponse[]> {
    return this.httpClient.get<DeviceResponse[]>(`${this.apiBase}/devices`);
  }

  getById(id: number): Observable<DeviceResponse> {
    return this.httpClient.get<DeviceResponse>(`${this.apiBase}/devices/${id}`);
  }

  create(request: DeviceRequest): Observable<DeviceResponse> {
    return this.httpClient.post<DeviceResponse>(
      `${this.apiBase}/devices`,
      request
    );
  }

  update(id: number, request: UpdateDeviceRequest): Observable<DeviceResponse> {
    return this.httpClient.put<DeviceResponse>(
      `${this.apiBase}/devices/${id}`,
      request
    );
  }

  delete(id: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.apiBase}/devices/${id}`);
  }

  getEvents(
    serialId: string,
    from?: Date,
    to?: Date,
    limit = 100
  ): Observable<DeviceEventResponse[]> {
    let params = new HttpParams().set('limit', limit.toString());

    if (from) {
      params = params.set('from', from.toISOString());
    }

    if (to) {
      params = params.set('to', to.toISOString());
    }

    return this.httpClient.get<DeviceEventResponse[]>(
      `${this.apiBase}/devices/${serialId}/events`,
      { params }
    );
  }

  getEventsPaged(
    query: DeviceEventsQuery
  ): Observable<PagedResult<DeviceEventResponse>> {
    let params = new HttpParams();

    if (query.from) {
      params = params.set('from', query.from.toISOString());
    }

    if (query.to) {
      params = params.set('to', query.to.toISOString());
    }

    if (query.search) {
      params = params.set('search', query.search);
    }

    if (query.page) {
      params = params.set('page', query.page.toString());
    }

    if (query.pageSize) {
      params = params.set('pageSize', query.pageSize.toString());
    }

    return this.httpClient.get<PagedResult<DeviceEventResponse>>(
      `${this.apiBase}/devices/${query.serialId}/events`,
      { params }
    );
  }

  getProjection(
    serialId: string,
    projectionType: string
  ): Observable<DeviceProjectionResponse> {
    return this.httpClient.get<DeviceProjectionResponse>(
      `${this.apiBase}/devices/${serialId}/projections/${projectionType}`
    );
  }

  getDistanceReadings(
    query: DistanceReadingsQuery
  ): Observable<PagedResult<DistanceReadingResponse>> {
    let params = new HttpParams();

    if (query.from) {
      params = params.set('from', query.from.toISOString());
    }

    if (query.to) {
      params = params.set('to', query.to.toISOString());
    }

    if (query.page) {
      params = params.set('page', query.page.toString());
    }

    if (query.pageSize) {
      params = params.set('pageSize', query.pageSize.toString());
    }

    return this.httpClient.get<PagedResult<DistanceReadingResponse>>(
      `${this.apiBase}/devices/${query.serialId}/distance-readings`,
      { params }
    );
  }
}
